/**
 * CollaboratorsBar — the collaboration strip on the Trip tab: who's on the
 * trip, plus (for owner/editor) an Invite action that mints a role link.
 *
 * The invite link points at the embed SPA's own origin with ?join=<token>;
 * opening it loads the SPA, which runs the join flow (preview → register →
 * join). Viewers see the roster but cannot invite.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useItinerary } from '../context/ItineraryContext';
import { useInstance } from '../context/InstanceContext';

type Role = 'editor' | 'viewer';

export default function CollaboratorsBar() {
  const { t } = useTranslation();
  const { myRole, members, refreshMembers, createInviteLink } = useItinerary();
  const { instanceId } = useInstance();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<Role>('editor');
  const [minting, setMinting] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const canInvite = myRole === 'owner' || myRole === 'editor';

  // Load the roster once when the panel opens (not on every Trip-tab paint).
  useEffect(() => {
    if (open) void refreshMembers();
  }, [open, refreshMembers]);

  const memberCount = members.length; // owner + members (0 until loaded)

  // The join link opens the embed SPA standalone, so it must carry the instance
  // (config resolution) alongside the token.
  const buildJoinUrl = (token: string) => {
    const p = new URLSearchParams();
    if (instanceId) p.set('instance', instanceId);
    p.set('join', token);
    return `${window.location.origin}/?${p.toString()}`;
  };

  const mint = async () => {
    setMinting(true);
    setCopied(false);
    try {
      const token = await createInviteLink(role);
      setLink(token ? buildJoinUrl(token) : null);
    } finally {
      setMinting(false);
    }
  };

  // Re-mint when the role toggle changes while the panel is open.
  const pickRole = (r: Role) => {
    setRole(r);
    setLink(null);
    setCopied(false);
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard?.writeText(link);
      setCopied(true);
    } catch {
      /* clipboard blocked — the field is selectable as a fallback */
    }
  };

  const summary = useMemo(() => {
    if (memberCount <= 1) return t('collab.just_you');
    return t('collab.people_count', { count: memberCount });
  }, [memberCount, t]);

  return (
    <div className="wg-collab">
      <button
        type="button"
        className="wg-collab__toggle"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span>{summary}</span>
      </button>

      {open && (
        <div className="wg-collab__panel">
          {members.length > 0 && (
            <ul className="wg-collab__members" role="list">
              {members.map(m => (
                <li key={m.user_id} className="wg-collab__member">
                  <span className="wg-collab__member-name">
                    {m.name || t('collab.someone')}
                  </span>
                  <span className="wg-collab__member-role">
                    {t(`collab.role_${m.role}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {canInvite ? (
            <div className="wg-collab__invite">
              <div
                className="wg-collab__roles"
                role="group"
                aria-label={t('collab.invite_as')}
              >
                <button
                  type="button"
                  className={`wg-collab__role${role === 'editor' ? ' is-active' : ''}`}
                  onClick={() => pickRole('editor')}
                >
                  {t('collab.role_editor')}
                </button>
                <button
                  type="button"
                  className={`wg-collab__role${role === 'viewer' ? ' is-active' : ''}`}
                  onClick={() => pickRole('viewer')}
                >
                  {t('collab.role_viewer')}
                </button>
              </div>

              {link ? (
                <div className="wg-collab__link-row">
                  <input
                    className="wg-collab__link"
                    readOnly
                    value={link}
                    onFocus={e => e.currentTarget.select()}
                  />
                  <button type="button" className="btn primary" onClick={copy}>
                    {copied ? t('collab.copied') : t('collab.copy')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn primary wg-collab__mint"
                  onClick={mint}
                  disabled={minting}
                >
                  {minting ? t('collab.minting') : t('collab.invite_link')}
                </button>
              )}
              <p className="wg-collab__note">{t('collab.invite_note')}</p>
            </div>
          ) : (
            <p className="wg-collab__note">{t('collab.viewer_note')}</p>
          )}
        </div>
      )}
    </div>
  );
}
